import type { SituacaoAcao, TipoArea } from '@/types/api';

/**
 * Os dois painéis. Decisões D01 e D02.
 *
 * POR QUE DOIS, E NÃO UM COM FILTRO
 *
 * As permissões `painel.cs` e `painel.produto` existem desde o começo e nunca eram
 * consultadas: havia uma tela só, igual para os dois lados. Mas as perguntas são
 * diferentes. O CS pergunta "o que eu pedi está andando?"; o Produto pergunta "o que
 * caiu para mim?". Mesma tabela, leituras opostas.
 *
 * O QUE SEPARA NÃO É O CONJUNTO DE DADOS
 *
 * O escopo de área já limita o que cada um vê, no servidor. O que cada painel muda é
 * quais situações valem a pena olhar e qual é o recorte inicial da tabela. Um rascunho
 * ainda não é assunto do Produto; uma ação concluída não é fila de ninguém.
 *
 * Nenhum dos dois amplia acesso. Painel é recorte dentro do que o escopo já permite.
 *
 * DUAS LISTAS, E NÃO UMA
 *
 * Havia um campo `situacoes` só, servindo ao mesmo tempo de recorte inicial e de teto do
 * filtro. O efeito era que, no Painel Produto, concluir uma ação a fazia desaparecer sem
 * nenhum clique capaz de trazê-la de volta: ela existia apenas no Painel CS. Quem acabou
 * de entregar algo perdia a entrega de vista na tela onde trabalha.
 *
 * Agora são duas. `situacoesPadrao` é o que abre; `situacoesVisiveis` é o que o painel
 * admite mostrar. A fila continua limpa por padrão e as encerradas continuam pertencendo
 * ao Produto, a um clique de distância.
 */

export type CodigoDoPainel = 'Cs' | 'Produto';

export interface Painel {
  codigo: CodigoDoPainel;
  /** Permissão que dá acesso. Verificada pelo servidor; aqui só decide desenhar o link. */
  permissao: string;
  titulo: string;
  /** Uma linha dizendo o que a tela responde. */
  apoio: string;
  /**
   * Recorte inicial da tabela, quando não há nada selecionado no filtro.
   *
   * É o que responde à pergunta do painel sem que ninguém clique em nada.
   */
  situacoesPadrao: SituacaoAcao[];
  /**
   * Tudo que o painel admite mostrar. Superconjunto de `situacoesPadrao`.
   *
   * É o conjunto de tiles e o teto do filtro. Situação fora daqui não tem tile e não
   * pode ser trazida por clique — é o que mantém rascunho fora do Painel Produto.
   */
  situacoesVisiveis: SituacaoAcao[];
  /**
   * Naturezas de área responsável que o painel mostra. Vazio significa todas.
   *
   * É o segundo eixo do recorte, independente da situação. O Painel Produto é a fila do
   * Produto: sem isto ele mostrava também COE, FAP Clínicas, Rede Credenciada, Suporte,
   * Equipe Implantação e LG, porque nunca filtrou área — só situação.
   *
   * Vai para o servidor, na lista e no resumo. Recortar no cliente daria a contagem da
   * página e o painel passaria a mentir a partir do item 51.
   */
  tiposDeAreaResponsavel: TipoArea[];
  /** O que o painel deixa de fora, para a tela dizer em vez de o usuário descobrir. */
  fora: string | undefined;
}

/**
 * Painel do CS: o ciclo inteiro do que foi pedido.
 *
 * Inclui as terminais de propósito. Quem abriu a solicitação precisa saber que ela foi
 * rejeitada ou concluída — é a resposta ao pedido, e é a informação que a planilha dava.
 */
const todasAsSituacoes: SituacaoAcao[] = [
  'Rascunho',
  'AguardandoAnalise',
  'EmAnalise',
  'AjusteSolicitado',
  'Aprovada',
  'Backlog',
  'EmDesenvolvimento',
  'Concluida',
  'Rejeitada',
  'Cancelada',
];

const painelDoCs: Painel = {
  codigo: 'Cs',
  permissao: 'painel.cs',
  titulo: 'Painel CS',
  apoio: 'O que foi solicitado e em que pé está, do rascunho ao encerramento.',
  situacoesPadrao: todasAsSituacoes,
  situacoesVisiveis: todasAsSituacoes,

  /*
    TODAS AS AREAS, e nao apenas as de natureza Cs.

    O Painel CS responde "o que eu pedi esta andando?". Uma solicitacao do CS direcionada
    ao COE tem area responsavel COE, e e justamente ela que o CS precisa acompanhar.
    Recortar por natureza aqui esconderia do solicitante o proprio pedido.

    E tambem onde as areas de apoio acompanham o trabalho delas enquanto nao tiverem um
    painel proprio.
  */
  tiposDeAreaResponsavel: [],
  fora: undefined,
};

/** O que o Produto tem a fazer: a fila propriamente dita. */
const filaDoProduto: SituacaoAcao[] = [
  'AguardandoAnalise',
  'EmAnalise',
  'Aprovada',
  'Backlog',
  'EmDesenvolvimento',
];

/**
 * Desfechos. Saem da fila, mas continuam sendo do Produto.
 *
 * `Rejeitada` e `Cancelada` entram junto de `Concluida` de propósito: os três são o mesmo
 * caso — a ação saiu da fila e quem trabalhou nela precisa poder revê-la sem trocar de
 * painel. Deixar só `Concluida` alcançável recriaria o problema nas outras duas.
 */
const terminaisDoProduto: SituacaoAcao[] = ['Concluida', 'Rejeitada', 'Cancelada'];

/**
 * Painel do Produto: a fila de trabalho. Decisão D02.
 *
 * FORA DA FILA, E FORA DO PAINEL: rascunho e ajuste solicitado. Estão com o CS, e aparecer
 * aqui daria a impressão de que o Produto precisa agir.
 *
 * FORA DA FILA, MAS DENTRO DO PAINEL: as terminais. Não abrem na tabela, porque fila é o
 * que espera decisão ou entrega, mas têm tile e são filtráveis. Concluir deixou de ser
 * sinônimo de perder de vista.
 *
 * FORA POR ÁREA: tudo que não é responsabilidade de uma área de natureza Produto. O painel
 * nunca filtrou área, só situação, e por isso trazia a fila do COE, FAP Clínicas, Rede
 * Credenciada, Suporte, Equipe Implantação e LG junto da do Produto — em homologação são
 * 46 itens de outras áreas dentro das situações da fila, contra 114 do Produto.
 *
 * Essas áreas passam a ser acompanhadas pelo Painel CS, e é o CS que as movimenta. É
 * acomodação, não desenho final: o certo é um painel por área, e enquanto ele não existe o
 * perfil Área de apoio recebeu `painel.cs` para não ficar sem painel nenhum.
 */
const painelDoProduto: Painel = {
  codigo: 'Produto',
  permissao: 'painel.produto',
  titulo: 'Painel Produto',
  apoio: 'A fila de análise e execução do Produto: o que espera uma decisão ou entrega.',
  situacoesPadrao: filaDoProduto,
  situacoesVisiveis: [...filaDoProduto, ...terminaisDoProduto],
  tiposDeAreaResponsavel: ['Produto'],
  fora: 'Somente ações do Produto. As de outras áreas, os rascunhos e os ajustes solicitados ficam no Painel CS. Encerradas não abrem na fila: clique no tile para vê-las.',
};

export const paineis: Record<CodigoDoPainel, Painel> = {
  Cs: painelDoCs,
  Produto: painelDoProduto,
};

/**
 * Situações que o painel mostra mas não abre por padrão.
 *
 * A tela usa isto para marcar esses tiles, senão o total do painel não fecharia com a
 * contagem da tabela e pareceria defeito em vez de recorte.
 */
export function situacoesForaDoPadrao(painel: Painel): SituacaoAcao[] {
  return painel.situacoesVisiveis.filter((s) => !painel.situacoesPadrao.includes(s));
}

/**
 * Situações que a tabela deve pedir ao servidor.
 *
 * Mora aqui, e não dentro da tela, porque é a regra que já quebrou uma vez: enquanto
 * `situacoesPadrao` e `situacoesVisiveis` eram um campo só, esta interseção recortava
 * contra o próprio recorte inicial e tornava as terminais inalcançáveis no Painel Produto.
 * Como função pura ela tem teste; embutida no componente, não tinha.
 *
 * Sem seleção vale o padrão. Com seleção vale a interseção com o visível — o clique amplia
 * até o limite do painel e não além, e é isso que mantém rascunho fora da fila.
 *
 * Seleção que não sobrou nada cai de volta no padrão: pedir lista vazia ao servidor traria
 * TODAS as situações do escopo, o oposto do que o clique pedia.
 */
export function situacoesEfetivas(
  painel: Painel,
  selecionadas: readonly SituacaoAcao[],
): SituacaoAcao[] {
  if (selecionadas.length === 0) {
    return painel.situacoesPadrao;
  }

  const permitidas = selecionadas.filter((s) => painel.situacoesVisiveis.includes(s));

  return permitidas.length === 0 ? painel.situacoesPadrao : permitidas;
}
